import { useFormik, Form, FormikProvider } from 'formik';
import { Button, Input, Select } from 'styled';
import InputField from 'components/formik/InputField';
import Model from 'components/Modal';
import { createDocument, getAllData } from 'apis/customApiHandler';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import axios from 'apis/axios';
import { useCallback, useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import { formSelectStyles } from 'utils/reactSelectStyles';
import { saveErrorCreationEvent, emailDomain } from "../../utils/analytics"
import { ANALYTICS_EVENTS } from 'utils/analyticsEvents';
import { useLocation } from 'react-router-dom';

const AddUser = ({ handleClick }) => {
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const { data: userRoles } = useQuery([`/roles`], () => getAllData('/roles'));
  const { data: allFleets } = useQuery([`/customer-fleet`], () =>
    getAllData('/customer-fleet', {
      fromUsers: 1,
      limit: 3000,
    })
  );
  const { data: allHubs } = useQuery([`/customer-hub`], () =>
    getAllData('/customer-hub', {
      fromUsers: 1,
      limit: 3000,
    })
  );

  const location = useLocation();

  function userCreationErrorEventFunc(eventName,modelName,params){
    return saveErrorCreationEvent(eventName,modelName,"Users",location.pathname,params);
  }

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      userRole: '',
      pin: '',
      phone: '',
      fleets: [],
      hubs: [],
    },
    onSubmit: async (values) => {
      try {

        const availableHubOptions = getHubOptions()?.filter((opt) => opt?.value !== 'all') ?? [];
        const eventParams = {
          email_domain: emailDomain(values.email),
          role_name: userRoles?.data?.find((item) => item._id === values.userRole)?.name,
          fleets: values?.fleets?.length === allFleets?.data?.length ? "All" : allFleets?.data?.filter((item) => values.fleets.some((fleet) => fleet === item._id))?.map((i) => i.fleetName),
          hubs: values?.hubs?.length === availableHubOptions.length ? "All" : allHubs?.data?.filter((item) => values.hubs.some((hub) => hub === item._id))?.map((i) => i.hubName)
        }
        userCreationErrorEventFunc(ANALYTICS_EVENTS.CLICK_SAVE_CHANGES,"Add User",eventParams);
        const data = await createDocument('user/register', {
          ...values,
        });
        if (data) {
          userCreationErrorEventFunc(ANALYTICS_EVENTS.SYS_ADD_USER_SUCCESS,"Add User",eventParams);
          toast.success('Successfully saved');
          queryClient.invalidateQueries('users');
          handleClick();
        }
      } catch (error) {
        const errMsg = error?.response?.data?.message || error?.message;
        userCreationErrorEventFunc(ANALYTICS_EVENTS.SYS_ADD_USER_ERROR, "Add User", { errMsg });
        return null;
      }
    },

    validate: (values) => {
      let errors = {};
      if (!values.name) errors.name = 'Required';
      if (!values.email) errors.email = 'Required';
      if (!values.userRole) errors.userRole = 'Required';
      if (!values.pin) errors.pin = 'Required';
      if (!values.phone) errors.phone = 'Required';
      if (!values.fleets || !values.fleets.length) errors.fleets = 'Required';
      if (!values.hubs || !values.hubs.length) errors.hubs = 'Required';
      return errors;
    },
  });

  const getFleetOptions = useCallback(() => {
    if (allFleets && allFleets.data && allFleets.data.length) {
      return [
        {
          label: 'Select All',
          value: 'all',
        },
        ...allFleets?.data?.map((fleet) => {
          return {
            value: fleet._id,
            label: fleet.fleetName,
          };
        }),
      ];
    }
  }, [allFleets]);

  const getHubOptions = useCallback(() => {
    if (allHubs && allHubs.data && allHubs.data.length) {
      return [
        formik?.values?.fleets?.length && {
          label: 'Select All',
          value: 'all',
        },
        ...(allHubs?.data?.length &&
          allHubs?.data
            ?.filter((hub) => {
              const selectedFleets = allFleets?.data?.filter((fleet) =>
                formik.values.fleets?.includes(fleet._id)
              );

              return selectedFleets?.some(
                (fleet) => fleet.fleetCode === hub.fleetCode
              );
            })
            ?.map((hub) => {
              return {
                value: hub._id,
                label: hub.hubName,
              };
            })),
      ];
    }
  }, [allFleets, allHubs, formik]);

  return (
    <Model handleClick={handleClick} title={'Add User'} className="max-w-2xl">
      <FormikProvider value={formik}>
        <Form className="px-5 text-gray-700 font-medium text-base grid lg:gap-x-14 gap-y-5 grid-flow-row grid-cols-1 lg:grid-cols-2 w-full h-full">
          <InputField type="text" label="Full Name" name="name" />
          <InputField type="text" label="Email" name="email" />
          <div className=" flex flex-col justify-center items-start gap-0 text-gray-600 font-normal">
            <label>User Roles</label>
            <Select
              name="userRole"
              value={formik.values.userRole}
              onChange={formik.handleChange}
            >
              <option>Select Roles</option>
              {userRoles?.data?.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </Select>
            {formik.touched.userRole && formik.errors.userRole && (
              <p className="text-red-500 font-normal">
                {formik.errors.userRole}
              </p>
            )}
          </div>
          <InputField type="text" label="Phone" name="phone" />
          <InputField type="text" label="Password" name="pin" />

          <div className="flex flex-col justify-center gap-0 self-start text-gray-600 font-normal">
            <label className="py-3 flex flex-col gap-2">
              Select Fleets
              <ReactSelect
                styles={formSelectStyles}
                className="shadow-md rounded-md"
                options={getFleetOptions()}
                value={
                  formik.values.fleets &&
                  formik.values.fleets?.map((fleet) => {
                    return {
                      value: fleet,
                      label: allFleets?.data?.find((item) => item._id === fleet)
                        ?.fleetName,
                    };
                  })
                }
                onChange={(value) => {
                  const allValues = value?.map((item) => item.value);
                  //console.log(value);

                  if (allValues.includes('all')) {
                    formik.setFieldValue(
                      'fleets',
                      allFleets?.data?.map((item) => {
                        return item._id;
                      })
                    );
                  } else {
                    let selectedFleets = allFleets?.data?.filter((fleet) =>
                      value?.map((item) => item.value)?.includes(fleet._id)
                    );

                    let selectedHubs = allHubs?.data?.filter((hub) =>
                      formik?.values?.hubs?.map((item) => item)?.includes(hub._id)
                    );

                    //remove hubs from selected fleets where
                    formik.setFieldValue(
                      'hubs',
                      selectedHubs
                        ?.filter((hub) =>
                          selectedFleets.find(
                            (fleet) => fleet.fleetCode === hub.fleetCode
                          )
                        )
                        .map((hub) => hub._id)
                    );

                    formik.setFieldValue(
                      'fleets',
                      value.map((fleet) => fleet.value)
                    );
                  }
                }}
                name="fleets"
                isMulti={true}
              />
              {formik.touched.fleets && formik.errors.fleets ? (
                <p className="text-red-500 font-normal">
                  *{formik.errors.fleets}
                </p>
              ) : null}
            </label>
          </div>

          <div className="flex flex-col justify-center gap-0 self-start text-gray-600 font-normal">
            <label className="py-3 flex flex-col gap-2">
              Select Hubs
              <ReactSelect
                styles={formSelectStyles}
                className="shadow-md rounded-md"
                options={getHubOptions()}
                value={
                  formik.values.hubs &&
                  formik.values.hubs?.map((hub) => {
                    return {
                      value: hub,
                      label: allHubs?.data?.find((item) => item._id === hub)
                        ?.hubName,
                    };
                  })
                }
                onChange={(value) => {
                  const allValues = value?.map((item) => item.value);

                  if (allValues.includes('all')) {
                    formik.setFieldValue(
                      'hubs',
                      allHubs?.data
                        ?.filter((hub) => {
                          const selectedFleets = allFleets?.data?.filter(
                            (fleet) => formik.values.fleets?.includes(fleet._id)
                          );

                          return selectedFleets?.some(
                            (fleet) => fleet.fleetCode === hub.fleetCode
                          );
                        })
                        ?.map((hub) => hub._id)
                    );
                  } else {
                    formik.setFieldValue(
                      'hubs',
                      value.map((hub) => hub.value)
                    );
                  }
                }}
                name="hubs"
                isMulti={true}
              />
              {formik.touched.hubs && formik.errors.hubs ? (
                <p className="text-red-500 font-normal">*{formik.errors.hubs}</p>
              ) : null}
            </label>
          </div>
        <div className="px-5 col-span-2 flex justify-end items-center gap-4 py-8 flex-wrap sm:w-full">
          <Button type="button" bgSecondary onClick={handleClick}>
            Cancel
          </Button>
          <Button type="button" bgPrimary onClick={formik.handleSubmit}>
            Save Changes
          </Button>
        </div>
        </Form>
      </FormikProvider>
    </Model>
  );
};

export default AddUser;
